import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAllTasks } from '@/hooks/useMyTasks';
import { TaskCard } from './TaskCard';
import { useNotepadEditor } from './useNotepadEditorState';
import { TasksEmptyState } from './TasksEmptyState';
import { Input } from '@/components/ui/input';
import { Search, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useTaskPreferences } from '@/hooks/useTaskPreferences';
import { shouldHideCompletedTask } from '@/types/taskPreferences';
import { celebrateTaskCompletion } from '@/lib/confetti';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { TaskCardSkeleton } from './TaskCardSkeleton';
import { EmptyState } from '@/components/empty/EmptyState';

/**
 * Tasks tab - search, filter, list tasks
 * Mirrors the Notes tab UX completely
 */
export function TasksTab() {
  const { t } = useTranslation(['notepad', 'common']);
  const [searchText, setSearchText] = useState('');
  const { openCreate } = useNotepadEditor();

  // Get task preferences
  const { data: preferences } = useTaskPreferences();

  // Track previous open task count for confetti trigger
  const prevOpenCountRef = useRef<number | null>(null);

  // Draft-first: open create editor without Firestore write
  const handleCreateTask = useCallback(() => {
    openCreate('task');
  }, [openCreate]);

  // Keyboard shortcut: Cmd/Ctrl + Shift + N to create new task
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'N') {
        e.preventDefault();
        handleCreateTask();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreateTask]);

  // Query all tasks (not deleted)
  const {
    data: allTasks,
    isLoading,
    error,
    refetch,
  } = useAllTasks({
    statuses: ['open', 'in_progress', 'blocked', 'done'],
    limit: 100,
  });

  // Filter and sort tasks based on preferences
  const tasks = useMemo(() => {
    if (!allTasks || !preferences) return allTasks;

    // 1. Filter by search text
    let filtered = searchText
      ? allTasks.filter((task) => task.title.toLowerCase().includes(searchText.toLowerCase()))
      : allTasks;

    // 2. Filter out hidden completed tasks
    filtered = filtered.filter((task) => {
      if (task.status !== 'done') return true;
      return !shouldHideCompletedTask(task.completedAt, preferences.hideCompletedAfter);
    });

    // 3. Sort based on preferences
    const sorted = [...filtered];

    switch (preferences.sortOrder) {
      case 'active-first':
        // Active tasks first, then completed (both sorted by updatedAt desc)
        sorted.sort((a, b) => {
          if (a.status === 'done' && b.status !== 'done') return 1;
          if (a.status !== 'done' && b.status === 'done') return -1;

          // Within same group, sort by updatedAt (newest first)
          const aTime = a.updatedAt?.toMillis() || 0;
          const bTime = b.updatedAt?.toMillis() || 0;
          return bTime - aTime;
        });
        break;

      case 'completed-first':
        // Completed tasks first, then active
        sorted.sort((a, b) => {
          if (a.status === 'done' && b.status !== 'done') return -1;
          if (a.status !== 'done' && b.status === 'done') return 1;

          const aTime = a.updatedAt?.toMillis() || 0;
          const bTime = b.updatedAt?.toMillis() || 0;
          return bTime - aTime;
        });
        break;

      case 'alphabetical':
        // Sort by title A-Z
        sorted.sort((a, b) => a.title.localeCompare(b.title));
        break;

      case 'custom-priority':
        // Sort by priority (high to low), then by updatedAt
        sorted.sort((a, b) => {
          const aPriority = a.priority || 0;
          const bPriority = b.priority || 0;

          if (aPriority !== bPriority) {
            return bPriority - aPriority; // Higher priority first
          }

          const aTime = a.updatedAt?.toMillis() || 0;
          const bTime = b.updatedAt?.toMillis() || 0;
          return bTime - aTime;
        });
        break;
    }

    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTasks, searchText, preferences?.sortOrder, preferences?.hideCompletedAfter]);

  // Count open tasks (not done)
  const openTasks = tasks?.filter((task) => task.status !== 'done') || [];
  const openTaskCount = openTasks.length;

  // Trigger confetti when last open task is completed
  useEffect(() => {
    if (!preferences?.enableCelebrations) return;

    // Check if we transitioned from ≥1 open tasks to 0
    if (prevOpenCountRef.current !== null && prevOpenCountRef.current >= 1 && openTaskCount === 0) {
      // Celebrate! 🎉
      void celebrateTaskCompletion();
    }

    // Update ref for next check
    prevOpenCountRef.current = openTaskCount;
  }, [openTaskCount, preferences?.enableCelebrations]);

  if (error) {
    return (
      <QueryErrorState
        title={t('notepad:errors.loadFailed', 'Failed to load tasks')}
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="px-6 pt-6">
        <TaskCardSkeleton count={5} />
      </div>
    );
  }

  const hasTasks = tasks && tasks.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header with New Task button */}
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('notepad:search.tasks', 'Search tasks...')}
              className="pl-9"
              aria-label={t('common:accessibility.searchTasks')}
            />
          </div>

          {/* New Task button */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={handleCreateTask}
              className="gap-2 shadow-sm"
              aria-label={t('common:accessibility.createTaskShortcut')}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">{t('notepad:actions.addTask', 'New Task')}</span>
            </Button>
          </motion.div>
        </div>

        {/* Divider */}
        <Separator />
      </div>

      {/* Content area with scroll */}
      <div className="flex-1 overflow-y-auto -mx-4 px-4">
        {/* Empty State - No open tasks */}
        <AnimatePresence mode="wait">
          {openTaskCount === 0 && preferences?.showEmptyState && !searchText && (
            <TasksEmptyState onCreateTask={handleCreateTask} />
          )}

          {/* Search empty state */}
          {!hasTasks && searchText && <EmptyState type="no-results" />}
        </AnimatePresence>

        {/* Tasks List */}
        {hasTasks && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="space-y-3"
          >
            <AnimatePresence>
              {tasks.map((task, index) => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05, duration: 0.2 }}
                >
                  <TaskCard task={task} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
}
